"use client";

import React, { useState } from "react";
import { 
  X, 
  Scale, 
  Building, 
  Gavel, 
  Calendar, 
  FileText, 
  UploadCloud, 
  CheckCircle2, 
  MessageSquare, 
  Send, 
  Download, 
  ShieldCheck, 
  FileCode,
  DollarSign,
  AlertTriangle,
  Clock,
  Printer
} from "lucide-react";
import { LegalCase, LegalDocument } from "@/lib/demos/legalData";

interface LegalDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  caseItem: LegalCase | null;
  userRole: "partner" | "associate" | "clerk";
  onUpdateCase: (updated: LegalCase) => void;
  onTriggerNotification?: (type: string, message: string) => void;
}

export default function LegalDetailModal({
  isOpen,
  onClose,
  caseItem,
  userRole,
  onUpdateCase,
  onTriggerNotification,
}: LegalDetailModalProps) {
  const [activeTab, setActiveTab] = useState<"overview" | "vault" | "generator" | "dispatch" | "ledger">("overview");
  const [newDocName, setNewDocName] = useState("");
  const [newDocType, setNewDocType] = useState<LegalDocument["type"]>("PETITION");
  const [newDocStatus, setNewDocStatus] = useState<LegalDocument["status"]>("Drafted");
  const [isGeneratingDoc, setIsGeneratingDoc] = useState(false);
  const [generatedDocType, setGeneratedDocType] = useState<"NOTICE" | "VAKALATNAMA" | "ENGAGEMENT">("NOTICE");
  const [dispatchStatus, setDispatchStatus] = useState<string | null>(null);

  if (!isOpen || !caseItem) return null;

  const handleAddDocument = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDocName.trim()) return;

    const newDoc: LegalDocument = {
      id: `doc-${Date.now()}`,
      name: newDocName.trim().endsWith(".pdf") ? newDocName.trim() : `${newDocName.trim()}.pdf`,
      type: newDocType,
      fileSize: "1.4 MB",
      uploadedAt: new Date().toISOString().split("T")[0],
      status: newDocStatus,
    };

    const updatedDocs = [newDoc, ...(caseItem.documents || [])];
    
    // Save to API
    fetch(`/api/demos/legal/cases/${caseItem.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ documents: updatedDocs }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.case) {
          onUpdateCase(data.case);
          setNewDocName("");
        }
      })
      .catch(console.error);
  };

  const handleDispatchNotification = async (channel: "WHATSAPP" | "SMS", template: "HEARING_REMINDER" | "DAILY_ORDER" | "RETAINER_INVOICE") => {
    let msg = "";
    if (template === "HEARING_REMINDER") {
      msg = `⚖️ [LexJuris Chambers] Urgent Reminder: Your matter "${caseItem.title}" (${caseItem.case_number}) is listed for hearing on ${caseItem.next_hearing_date} before ${caseItem.judge_bench} in ${caseItem.court_room} (Item #${caseItem.item_number}). Please report to chambers at 09:30 AM.`;
    } else if (template === "DAILY_ORDER") {
      msg = `⚖️ [LexJuris Chambers] Court Hearing Update: Matter "${caseItem.title}" was called before ${caseItem.judge_bench} today. Latest Order: "${caseItem.notes || 'Arguments heard in part. Matter adjourned for further hearing.'}". Certified copy will be uploaded to your client vault.`;
    } else {
      const amtStr = caseItem.currency === "USD" ? `$${caseItem.retainer_amount}` : `₹${caseItem.retainer_amount.toLocaleString("en-IN")}`;
      msg = `⚖️ [LexJuris Chambers] Retainer Invoice: Re: ${caseItem.title} (${caseItem.case_number}). Professional fees and out-of-pocket court filing expenses due: ${amtStr}. Pay securely via UPI / Wire: https://johnsonsoftwares.com/demos/legal`;
    }

    setDispatchStatus(`Sending ${channel} alert to ${caseItem.client_phone}...`);

    try {
      const res = await fetch("/api/demos/legal/notify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          caseId: caseItem.id,
          channel,
          template,
          customMessage: msg,
        }),
      });

      const data = await res.json();
      if (data.success) {
        setDispatchStatus(`✓ Successfully sent via ${channel} to ${caseItem.client_phone}!`);
      } else {
        setDispatchStatus(`Alert recorded for ${caseItem.client_phone}`);
      }
    } catch (err) {
      setDispatchStatus(`Dispatched alert to ${caseItem.client_phone}`);
    }

    if (onTriggerNotification) {
      onTriggerNotification(template, msg);
    }
    setTimeout(() => setDispatchStatus(null), 5000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-950/85 backdrop-blur-md overflow-y-auto animate-fade-in">
      <div className="relative w-full max-w-4xl p-5 sm:p-7 rounded-2xl bg-slate-900 border border-slate-800 shadow-2xl text-slate-100 my-8 max-h-[90vh] flex flex-col">
        
        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-colors z-10"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Modal Header */}
        <div className="pb-4 border-b border-slate-800">
          <div className="flex flex-wrap items-center gap-2 mb-1.5">
            <span className="font-mono text-amber-400 font-bold text-sm">
              {caseItem.case_number}
            </span>
            <span className="text-slate-500">•</span>
            <span className="font-mono text-slate-400 text-xs">
              CNR: {caseItem.cnr_number}
            </span>
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
              caseItem.urgency === "EMERGENCY"
                ? "bg-rose-500/10 text-rose-400 border-rose-500/20"
                : caseItem.urgency === "HIGH"
                ? "bg-amber-500/10 text-amber-400 border-amber-500/20"
                : "bg-blue-500/10 text-blue-400 border-blue-500/20"
            }`}>
              {caseItem.urgency} URGENCY
            </span>
          </div>

          <h2 className="text-lg sm:text-xl font-serif font-bold text-white line-clamp-1">
            {caseItem.title}
          </h2>
          <div className="flex flex-wrap items-center gap-4 text-xs text-slate-400 mt-1">
            <span>Client: <strong className="text-slate-200">{caseItem.client_name}</strong></span>
            <span>Opposing: <strong className="text-slate-200">{caseItem.opposing_party}</strong></span>
            <span>Domain: <strong className="text-amber-400">{caseItem.practice_area}</strong></span>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex items-center gap-1.5 py-3 border-b border-slate-800 overflow-x-auto text-xs">
          <button
            type="button"
            onClick={() => setActiveTab("overview")}
            className={`px-3 py-1.5 rounded-lg font-semibold transition-all whitespace-nowrap ${
              activeTab === "overview"
                ? "bg-amber-500 text-slate-950 font-bold shadow-sm"
                : "text-slate-400 hover:text-white hover:bg-slate-800"
            }`}
          >
            📋 Matter Docket
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("vault")}
            className={`px-3 py-1.5 rounded-lg font-semibold transition-all whitespace-nowrap flex items-center gap-1.5 ${
              activeTab === "vault"
                ? "bg-amber-500 text-slate-950 font-bold shadow-sm"
                : "text-slate-400 hover:text-white hover:bg-slate-800"
            }`}
          >
            <span>📁 Document Vault</span>
            <span className="px-1.5 py-0.2 rounded-full bg-slate-800 text-[10px] text-slate-300">
              {caseItem.documents?.length || 0}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("generator")}
            className={`px-3 py-1.5 rounded-lg font-semibold transition-all whitespace-nowrap ${
              activeTab === "generator"
                ? "bg-amber-500 text-slate-950 font-bold shadow-sm"
                : "text-slate-400 hover:text-white hover:bg-slate-800"
            }`}
          >
            ⚡ 1-Click Document Generator
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("dispatch")}
            className={`px-3 py-1.5 rounded-lg font-semibold transition-all whitespace-nowrap ${
              activeTab === "dispatch"
                ? "bg-amber-500 text-slate-950 font-bold shadow-sm"
                : "text-slate-400 hover:text-white hover:bg-slate-800"
            }`}
          >
            📲 Automated Client Alerts
          </button>
          {userRole === "partner" && (
            <button
              type="button"
              onClick={() => setActiveTab("ledger")}
              className={`px-3 py-1.5 rounded-lg font-semibold transition-all whitespace-nowrap ${
                activeTab === "ledger"
                  ? "bg-amber-500 text-slate-950 font-bold shadow-sm"
                  : "text-slate-400 hover:text-white hover:bg-slate-800"
              }`}
            >
              💰 Retainer Ledger (Partner Only)
            </button>
          )}
        </div>

        {/* Tab Body */}
        <div className="flex-1 overflow-y-auto py-4 space-y-4 text-xs pr-1">
          
          {/* TAB 1: OVERVIEW */}
          {activeTab === "overview" && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="p-3 rounded-xl bg-slate-850 border border-slate-750">
                  <div className="text-[10px] uppercase font-bold text-slate-500">Court Forum</div>
                  <div className="text-sm font-bold text-white mt-1">{caseItem.court_name}</div>
                  <div className="text-[11px] text-amber-400 mt-0.5">{caseItem.court_room}</div>
                </div>

                <div className="p-3 rounded-xl bg-slate-850 border border-slate-750">
                  <div className="text-[10px] uppercase font-bold text-slate-500">Presiding Bench</div>
                  <div className="text-sm font-bold text-white mt-1 line-clamp-1">{caseItem.judge_bench}</div>
                  <div className="text-[11px] text-slate-400 mt-0.5">Item #{caseItem.item_number} on Board</div>
                </div>

                <div className="p-3 rounded-xl bg-slate-850 border border-slate-750">
                  <div className="text-[10px] uppercase font-bold text-slate-500">Next Hearing Date (NDOH)</div>
                  <div className="text-sm font-bold text-emerald-400 font-mono mt-1">{caseItem.next_hearing_date}</div>
                  <div className="text-[11px] text-slate-400 mt-0.5">Stage: {caseItem.stage}</div>
                </div>
              </div>

              {/* Counsel & Conflict Check */}
              <div className="p-3.5 rounded-xl bg-slate-850/80 border border-slate-750 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Designated Lead Counsel:</span>
                  <span className="font-semibold text-white">{caseItem.lead_counsel}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Client Secure Contact:</span>
                  <span className="font-mono text-slate-200">{caseItem.client_phone} • {caseItem.client_email}</span>
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-slate-750">
                  <span className="text-slate-400">Conflict-of-Interest Status:</span>
                  <span className="text-emerald-400 font-semibold flex items-center gap-1">
                    <ShieldCheck className="w-3.5 h-3.5" /> Cleared (No Adverse Brief)
                  </span>
                </div>
              </div>

              {/* Notes & Order Sheet History */}
              <div className="p-3.5 rounded-xl bg-slate-850/60 border border-slate-750 space-y-1.5">
                <div className="text-[10px] uppercase font-bold text-slate-400">
                  Case Brief & Latest Order Sheet Directions:
                </div>
                <p className="text-slate-300 leading-relaxed italic bg-slate-900/50 p-2.5 rounded-lg border border-slate-800">
                  "{caseItem.notes || 'No interim orders entered.'}"
                </p>
              </div>

              {/* Court Appearances List */}
              {caseItem.appearances && caseItem.appearances.length > 0 && (
                <div className="space-y-2">
                  <div className="text-[10px] uppercase font-bold text-slate-400">
                    Past Hearing Appearances & Daily Orders:
                  </div>
                  {caseItem.appearances.map((app, idx) => (
                    <div key={idx} className="p-3 rounded-xl bg-slate-900 border border-slate-800 space-y-1">
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="font-bold text-amber-400">{app.date} • {app.purpose}</span>
                        <span className="text-slate-400">{app.court} (Item #{app.itemNumber})</span>
                      </div>
                      <p className="text-slate-300 text-[11px]">{app.orderSummary}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB 2: DOCUMENT VAULT */}
          {activeTab === "vault" && (
            <div className="space-y-4">
              {/* Add Document Form */}
              <form onSubmit={handleAddDocument} className="p-3.5 rounded-xl bg-slate-850 border border-slate-750 space-y-3">
                <div className="font-semibold text-white flex items-center gap-2">
                  <UploadCloud className="w-4 h-4 text-amber-400" />
                  <span>Ingest / Record Document to Case Binder</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                  <div>
                    <label className="block text-[10px] text-slate-400 mb-1">Document Title</label>
                    <input
                      type="text"
                      value={newDocName}
                      onChange={(e) => setNewDocName(e.target.value)}
                      placeholder="e.g. Rejoinder_Affidavit_Vol2"
                      className="w-full px-2.5 py-1.5 bg-slate-900 border border-slate-700 rounded-lg text-xs text-white"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] text-slate-400 mb-1">Category</label>
                    <select
                      value={newDocType}
                      onChange={(e) => setNewDocType(e.target.value as any)}
                      className="w-full px-2.5 py-1.5 bg-slate-900 border border-slate-700 rounded-lg text-xs text-white"
                    >
                      <option value="PETITION">Plaint / Petition</option>
                      <option value="VAKALATNAMA">Vakalatnama (POA)</option>
                      <option value="AFFIDAVIT">Sworn Affidavit</option>
                      <option value="COURT_ORDER">Court Order Sheet</option>
                      <option value="EVIDENCE">Evidence / Annexure</option>
                      <option value="INVOICE">Tax Invoice</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] text-slate-400 mb-1">Registry Status</label>
                    <select
                      value={newDocStatus}
                      onChange={(e) => setNewDocStatus(e.target.value as any)}
                      className="w-full px-2.5 py-1.5 bg-slate-900 border border-slate-700 rounded-lg text-xs text-white"
                    >
                      <option value="Drafted">Drafted</option>
                      <option value="Client Signed">Client Signed</option>
                      <option value="Filed in Registry">Filed in Registry</option>
                      <option value="Certified Copy Issued">Certified Copy Issued</option>
                    </select>
                  </div>
                </div>
                <div className="flex justify-end">
                  <button
                    type="submit"
                    className="px-4 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs transition-colors"
                  >
                    + Add to Vault
                  </button>
                </div>
              </form>

              {/* Documents List */}
              <div className="space-y-2">
                <div className="text-[10px] uppercase font-bold text-slate-400">
                  Encrypted Documents ({caseItem.documents?.length || 0})
                </div>
                {(!caseItem.documents || caseItem.documents.length === 0) ? (
                  <div className="p-8 text-center rounded-xl bg-slate-900/60 border border-slate-800 text-slate-500">
                    No documents uploaded yet to this matter vault.
                  </div>
                ) : (
                  caseItem.documents.map((doc) => (
                    <div
                      key={doc.id}
                      className="p-3 rounded-xl bg-slate-850/80 border border-slate-750 flex items-center justify-between gap-3"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-amber-500/10 text-amber-400">
                          <FileText className="w-4 h-4" />
                        </div>
                        <div>
                          <div className="font-semibold text-white">{doc.name}</div>
                          <div className="text-[10px] text-slate-400">
                            {doc.type} • {doc.fileSize} • Uploaded {doc.uploadedAt}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md border ${
                          doc.status === "Certified Copy Issued"
                            ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                            : doc.status === "Filed in Registry"
                            ? "bg-blue-500/10 text-blue-400 border-blue-500/20"
                            : "bg-amber-500/10 text-amber-400 border-amber-500/20"
                        }`}>
                          {doc.status}
                        </span>

                        <button
                          type="button"
                          onClick={() => alert(`Simulated downloading ${doc.name}`)}
                          className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-750 text-slate-300 hover:text-white"
                          title="Download Encrypted Copy"
                        >
                          <Download className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* TAB 3: 1-CLICK DOCUMENT GENERATOR */}
          {activeTab === "generator" && (
            <div className="space-y-4">
              <div className="p-3.5 rounded-xl bg-slate-850 border border-slate-750 space-y-3">
                <div className="font-semibold text-white">Select Automated Template to Generate</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => setGeneratedDocType("NOTICE")}
                    className={`p-3 rounded-xl border text-left transition-all ${
                      generatedDocType === "NOTICE"
                        ? "border-amber-500 bg-amber-500/10 text-white"
                        : "border-slate-800 bg-slate-900 text-slate-400 hover:text-slate-200"
                    }`}
                  >
                    <div className="font-bold text-xs">📜 Legal Demand Notice</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">Section 138 NI / Specific Relief notice</div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setGeneratedDocType("VAKALATNAMA")}
                    className={`p-3 rounded-xl border text-left transition-all ${
                      generatedDocType === "VAKALATNAMA"
                        ? "border-amber-500 bg-amber-500/10 text-white"
                        : "border-slate-800 bg-slate-900 text-slate-400 hover:text-slate-200"
                    }`}
                  >
                    <div className="font-bold text-xs">⚖️ High Court Vakalatnama</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">Power of Attorney with bar welfare stamp</div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setGeneratedDocType("ENGAGEMENT")}
                    className={`p-3 rounded-xl border text-left transition-all ${
                      generatedDocType === "ENGAGEMENT"
                        ? "border-amber-500 bg-amber-500/10 text-white"
                        : "border-slate-800 bg-slate-900 text-slate-400 hover:text-slate-200"
                    }`}
                  >
                    <div className="font-bold text-xs">📑 Retainer Agreement</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">Professional engagement & fee schedule</div>
                  </button>
                </div>
              </div>

              {/* Document Preview Box */}
              <div className="p-5 rounded-xl bg-slate-950 border border-slate-800 font-mono text-[11px] leading-relaxed text-slate-300 space-y-3 shadow-inner">
                <div className="text-center font-bold text-amber-400 border-b border-slate-800 pb-2">
                  {generatedDocType === "NOTICE" && "FORMAL LEGAL DEMAND NOTICE • UNDER INSTRUCTIONS OF CLIENT"}
                  {generatedDocType === "VAKALATNAMA" && "VAKALATNAMA • IN THE HIGH COURT OF DELHI AT NEW DELHI"}
                  {generatedDocType === "ENGAGEMENT" && "CLIENT ENGAGEMENT LETTER & TERMS OF RETAINER"}
                </div>

                <div className="space-y-1 text-slate-400">
                  <div><strong>DOCKET REF:</strong> {caseItem.case_number} | <strong>CNR:</strong> {caseItem.cnr_number}</div>
                  <div><strong>CLIENT:</strong> {caseItem.client_name}</div>
                  <div><strong>OPPOSING:</strong> {caseItem.opposing_party}</div>
                  <div><strong>FORUM:</strong> {caseItem.court_name} ({caseItem.court_room})</div>
                </div>

                <p className="pt-2 text-slate-200">
                  {generatedDocType === "NOTICE" &&
                    `TAKE NOTICE that on behalf of our client, ${caseItem.client_name}, we hereby call upon you to cease and desist all illegal actions concerning ${caseItem.title} within 15 days of receipt of this notice, failing which our client shall initiate appropriate legal proceedings before the competent court at your sole risk, cost, and consequences.`}
                  {generatedDocType === "VAKALATNAMA" &&
                    `KNOW ALL to whom these presents shall come that I/We, ${caseItem.client_name}, do hereby appoint and retain ${caseItem.lead_counsel} to be our lawful Advocate(s) in the above matter pending in ${caseItem.court_name} to appear, conduct, plead, and act on my/our behalf in all proceedings.`}
                  {generatedDocType === "ENGAGEMENT" &&
                    `This agreement confirms that LexJuris Associates is retained by ${caseItem.client_name} for legal representation in ${caseItem.title}. The initial agreed retainer is ${caseItem.currency === "USD" ? `$${caseItem.retainer_amount}` : `₹${caseItem.retainer_amount.toLocaleString("en-IN")}`}, exclusive of actual out-of-pocket court filing fees and senior counsel briefing charges.`}
                </p>

                <div className="pt-3 border-t border-slate-800 flex items-center justify-between text-slate-500 text-[10px]">
                  <span>LexJuris Digital Stamp • Certified Drafting Engine</span>
                  <span>Advocate Code: D/1892/2026</span>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => alert(`Simulated printing ${generatedDocType} PDF`)}
                  className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-750 text-white font-bold text-xs flex items-center gap-1.5"
                >
                  <Printer className="w-3.5 h-3.5" />
                  <span>Print Document</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const newDocName = `${generatedDocType}_${caseItem.client_name.replace(/\s+/g, "_")}.pdf`;
                    const updatedDocs = [
                      {
                        id: `doc-${Date.now()}`,
                        name: newDocName,
                        type: (generatedDocType === "NOTICE" ? "PETITION" : generatedDocType === "VAKALATNAMA" ? "VAKALATNAMA" : "INVOICE") as any,
                        fileSize: "1.2 MB",
                        uploadedAt: new Date().toISOString().split("T")[0],
                        status: "Drafted" as const,
                      },
                      ...(caseItem.documents || []),
                    ];
                    fetch(`/api/demos/legal/cases/${caseItem.id}`, {
                      method: "PUT",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ documents: updatedDocs }),
                    })
                      .then((res) => res.json())
                      .then((data) => {
                        if (data.case) {
                          onUpdateCase(data.case);
                          alert(`Saved ${newDocName} directly to Case Vault!`);
                        }
                      });
                  }}
                  className="px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs flex items-center gap-1.5"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>Save to Document Vault</span>
                </button>
              </div>
            </div>
          )}

          {/* TAB 4: AUTOMATED CLIENT NOTIFICATIONS */}
          {activeTab === "dispatch" && (
            <div className="space-y-4">
              {dispatchStatus && (
                <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 shrink-0" />
                  <span>{dispatchStatus}</span>
                </div>
              )}

              <div className="p-3.5 rounded-xl bg-slate-850 border border-slate-750 space-y-1">
                <div className="font-semibold text-white">Client Automated Notification Triggers</div>
                <p className="text-[11px] text-slate-400">
                  Instant WhatsApp and SMS dispatch engine directly linked to court cause list updates.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {/* 1. Hearing Date Reminder */}
                <div className="p-4 rounded-xl bg-slate-900 border border-slate-800 flex flex-col justify-between space-y-3">
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-1.5 text-amber-400 font-bold text-xs">
                      <Calendar className="w-4 h-4" />
                      <span>Hearing Date Alert</span>
                    </div>
                    <p className="text-[11px] text-slate-400 leading-relaxed">
                      Sends courtroom number, presiding judge bench, and item number on board 24h prior.
                    </p>
                    <div className="p-2 rounded bg-slate-950 font-mono text-[10px] text-slate-400 border border-slate-800/80">
                      NDOH: {caseItem.next_hearing_date} • Room {caseItem.court_room}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => handleDispatchNotification("WHATSAPP", "HEARING_REMINDER")}
                      className="flex-1 py-1.5 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 border border-emerald-500/30 font-bold text-[11px] flex items-center justify-center gap-1"
                    >
                      <MessageSquare className="w-3 h-3" />
                      <span>WhatsApp</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDispatchNotification("SMS", "HEARING_REMINDER")}
                      className="flex-1 py-1.5 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-500/30 font-bold text-[11px] flex items-center justify-center gap-1"
                    >
                      <Send className="w-3 h-3" />
                      <span>SMS</span>
                    </button>
                  </div>
                </div>

                {/* 2. Daily Order Sheet Dispatch */}
                <div className="p-4 rounded-xl bg-slate-900 border border-slate-800 flex flex-col justify-between space-y-3">
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-1.5 text-blue-400 font-bold text-xs">
                      <FileText className="w-4 h-4" />
                      <span>Daily Order Sheet</span>
                    </div>
                    <p className="text-[11px] text-slate-400 leading-relaxed">
                      Immediate summary of court proceedings, interim stays, and directions issued today.
                    </p>
                    <div className="p-2 rounded bg-slate-950 font-mono text-[10px] text-slate-400 border border-slate-800/80 truncate">
                      "{caseItem.notes || 'Arguments heard in part.'}"
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => handleDispatchNotification("WHATSAPP", "DAILY_ORDER")}
                      className="flex-1 py-1.5 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 border border-emerald-500/30 font-bold text-[11px] flex items-center justify-center gap-1"
                    >
                      <MessageSquare className="w-3 h-3" />
                      <span>WhatsApp</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDispatchNotification("SMS", "DAILY_ORDER")}
                      className="flex-1 py-1.5 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-500/30 font-bold text-[11px] flex items-center justify-center gap-1"
                    >
                      <Send className="w-3 h-3" />
                      <span>SMS</span>
                    </button>
                  </div>
                </div>

                {/* 3. Retainer / Invoicing Payment Link */}
                <div className="p-4 rounded-xl bg-slate-900 border border-slate-800 flex flex-col justify-between space-y-3">
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-1.5 text-emerald-400 font-bold text-xs">
                      <DollarSign className="w-4 h-4" />
                      <span>Retainer / Court Fees</span>
                    </div>
                    <p className="text-[11px] text-slate-400 leading-relaxed">
                      Dispatches itemized billing invoice link for filing stamps and senior counsel appearance.
                    </p>
                    <div className="p-2 rounded bg-slate-950 font-mono text-[10px] text-emerald-400 font-bold border border-slate-800/80">
                      Due: {caseItem.currency === "USD" ? `$${caseItem.retainer_amount}` : `₹${caseItem.retainer_amount.toLocaleString("en-IN")}`}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => handleDispatchNotification("WHATSAPP", "RETAINER_INVOICE")}
                      className="flex-1 py-1.5 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 border border-emerald-500/30 font-bold text-[11px] flex items-center justify-center gap-1"
                    >
                      <MessageSquare className="w-3 h-3" />
                      <span>WhatsApp</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDispatchNotification("SMS", "RETAINER_INVOICE")}
                      className="flex-1 py-1.5 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-500/30 font-bold text-[11px] flex items-center justify-center gap-1"
                    >
                      <Send className="w-3 h-3" />
                      <span>SMS</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 5: FINANCIAL LEDGER (PARTNER ONLY) */}
          {activeTab === "ledger" && userRole === "partner" && (
            <div className="space-y-4">
              <div className="p-3.5 rounded-xl bg-slate-850 border border-slate-750 flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase font-bold text-slate-400">Managing Partner Financial Overview</div>
                  <div className="text-sm font-bold text-white mt-0.5">Matter Retainer & Appearance Account</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] text-slate-400">Total Account Value:</div>
                  <div className="text-base font-bold text-emerald-400 font-mono">
                    {caseItem.currency === "USD"
                      ? `$${caseItem.total_billed_amount || caseItem.retainer_amount}`
                      : `₹${(caseItem.total_billed_amount || caseItem.retainer_amount).toLocaleString("en-IN")}`}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="p-3.5 rounded-xl bg-slate-900 border border-slate-800 space-y-2">
                  <div className="font-semibold text-white">Retainer Deposit Paid</div>
                  <div className="text-lg font-bold text-amber-400 font-mono">
                    {caseItem.currency === "USD" ? `$${caseItem.retainer_amount}` : `₹${caseItem.retainer_amount.toLocaleString("en-IN")}`}
                  </div>
                  <p className="text-[11px] text-slate-400">
                    Advance deposited into chambers client account for petition drafting & preliminary admission.
                  </p>
                </div>

                <div className="p-3.5 rounded-xl bg-slate-900 border border-slate-800 space-y-2">
                  <div className="font-semibold text-white">Out-of-Pocket Court Expenses</div>
                  <div className="text-lg font-bold text-blue-400 font-mono">
                    {caseItem.currency === "USD" ? "$450.00" : "₹18,500"}
                  </div>
                  <p className="text-[11px] text-slate-400">
                    Registry filing stamps, court process server fees, certified order copies, and senior clerk fees.
                  </p>
                </div>
              </div>
            </div>
          )}

        </div>

        {/* Modal Footer */}
        <div className="pt-3 border-t border-slate-800 flex items-center justify-between text-[11px] text-slate-500">
          <span>LexJuris Chambers Case ID: #{caseItem.id}</span>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 font-semibold"
          >
            Close Binder
          </button>
        </div>

      </div>
    </div>
  );
}
